import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { AlertButton, AlertController, IonItemSliding, IonReorderGroup, ItemReorderEventDetail, ModalController, IonicModule } from '@ionic/angular';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { Tile } from 'src/app/components/tile/tile.component';
import { isPlatform } from '@ionic/angular';
import { HomescreenService } from './homescreen.service';
import { AppState } from 'src/app/app.module';
import { HomescreenSetting, HomescreenSettings } from 'src/app/ngrx/settings/settings.models';
import { settingsFeature } from 'src/app/ngrx/settings/settings.feature';
import { SettingsActions } from 'src/app/ngrx/settings/settings.actions';

@Component({
    selector: 'app-homescreen',
    templateUrl: './homescreen.page.html',
    styleUrls: ['./homescreen.page.scss'],
    imports: [IonicModule, TranslatePipe]
})
export class HomescreenPage implements OnInit {
  modalController = inject(ModalController);
  translate = inject(TranslateService);
  private store = inject<Store<AppState>>(Store);
  private homescreenService = inject(HomescreenService);
  private alertController = inject(AlertController);

  private subscriptions = new Subscription();
  private homescreenItems$: Observable<HomescreenSettings>;

  @ViewChild(IonReorderGroup)
  public reorderGroup!: IonReorderGroup;

  public homescreenItems: HomescreenSettings = {};
  protected visibleTiles: Tile[] = [];
  protected invisibleTiles: Tile[] = [];

  public reorderIcon = 'reorder-three-outline';

  constructor() {
    this.homescreenItems$ = this.store.pipe(select(settingsFeature.selectHomescreen));
    this.reorderIcon = isPlatform('ios') ? 'reorder-three-outline' : 'reorder-two-outline';
  }

  public handleReorder(event: CustomEvent<ItemReorderEventDetail>) {
    const updatedTiles: Tile[] = event.detail.complete(this.visibleTiles);
    const updatedHomescreen: HomescreenSettings = this.homescreenService.visibleTilesToHomescreenSettings(this.homescreenItems, updatedTiles);
    this.store.dispatch(SettingsActions.homescreenUpdated({ homescreen: { ...updatedHomescreen } }));
  }

  public ngOnInit() {
    this.subscriptions.add(this.homescreenItems$.subscribe((homescreenItems) => {
      if(!homescreenItems) { // This is propably the first start of the App. Initialize App with all available Tiles

        const homescreen: HomescreenSettings = this.homescreenService.getAvailableHomescreenSettings();
        this.store.dispatch(SettingsActions.homescreenUpdated({ homescreen }));
        this.homescreenItems = homescreen
      } else {
        this.homescreenItems = homescreenItems;
      }
      this.visibleTiles = this.homescreenService.getVisibleTiles(this.homescreenItems,);
      this.invisibleTiles = this.homescreenService.getInvisibleTiles(this.homescreenItems, this.translate);
    }));
  }

  public async removeItem(tileId: string, slidingItem?: IonItemSliding) {
    const homescreen: HomescreenSettings = this.homescreenService.setTilePosition(this.homescreenItems, String(tileId), null);
    this.store.dispatch(SettingsActions.homescreenUpdated({ homescreen }));
    if(slidingItem) {
      slidingItem.close();
    }
  }


  public async openAlert(tileId: string|number, itemSliding: IonItemSliding) {
    // Do not open dialog if only visible item (cannot move top, up, down, bottom or deactivate)
    if(Object.values(this.homescreenItems).filter((h: HomescreenSetting): boolean => h.position !== null && h.position !== undefined).length === 1) {
      return;
    }

    const visiblePositions = Object.values(this.homescreenItems)
      .map((tile: HomescreenSetting): number | null | undefined => tile.position)
      .filter((position): position is number => position !== null && position !== undefined);

    const buttons: AlertButton[] = [];
    // Add buttons if not in top position
    if(this.homescreenItems[tileId].position !== Math.min(...visiblePositions)) {
      buttons.push({
          text: this.translate.instant('homescreen.settings.alert.top'),
          handler: () => {
            const homescreen: HomescreenSettings = this.homescreenService.setTilePosition(this.homescreenItems, String(tileId), 0);
            this.store.dispatch(SettingsActions.homescreenUpdated({ homescreen }));
            itemSliding.close();
          }
      });
      buttons.push({
          text: this.translate.instant('homescreen.settings.alert.oneUp'),
          handler: () => {
            const homescreen: HomescreenSettings = this.homescreenService.setTilePosition(this.homescreenItems, String(tileId), this.homescreenItems[tileId].position! - 1);
            this.store.dispatch(SettingsActions.homescreenUpdated({ homescreen }));
            itemSliding.close();
          }
        });
    }
    // Add buttons if not in bottom position
    if(this.homescreenItems[tileId].position !== Math.max(...visiblePositions)) {
      buttons.push({
          text: this.translate.instant('homescreen.settings.alert.oneDown'),
          handler: () => {
            const homescreen: HomescreenSettings = this.homescreenService.setTilePosition(this.homescreenItems, String(tileId), this.homescreenItems[tileId].position! + 1);
            this.store.dispatch(SettingsActions.homescreenUpdated({ homescreen }));
            itemSliding.close();
          }
        });
      buttons.push({
          text: this.translate.instant('homescreen.settings.alert.bottom'),
          handler: () => {
            const homescreen: HomescreenSettings = this.homescreenService.setTilePosition(this.homescreenItems, String(tileId), Math.max(...visiblePositions));
            this.store.dispatch(SettingsActions.homescreenUpdated({ homescreen }));
            itemSliding.close();
          }
        });
    }
    buttons.push({
        text: this.translate.instant('homescreen.settings.alert.deactivate'),
        role: 'destructive',
        handler: async () => {
          const homescreen: HomescreenSettings = this.homescreenService.setTilePosition(this.homescreenItems, String(tileId), null);
          this.store.dispatch(SettingsActions.homescreenUpdated({ homescreen }));
          itemSliding.close();
        }
      });
    buttons.push({
        text: this.translate.instant('core.cancel'),
        role: 'cancel',
        handler: () => {
          itemSliding.close();
        }
      });

    const alert = await this.alertController.create({
      header: this.translate.instant('homescreen.settings.alert.title'),
      message: this.translate.instant('homescreen.settings.alert.message'),
      buttons
    });
    alert.present();
  }

  public async openAddAlert(tile: Tile) {
    const alert = await this.alertController.create({
      header: this.translate.instant('homescreen.settings.addAlert.title'),
      message: this.translate.instant('homescreen.settings.addAlert.message'),
      buttons: [
        {
          text: this.translate.instant('core.add'),
          handler: () => {
            const homescreen = this.homescreenService.setTilePosition(this.homescreenItems, String(tile.id), 0);
            this.store.dispatch(SettingsActions.homescreenUpdated({ homescreen }));
          }
        }, {
          text: this.translate.instant('core.cancel'),
          role: 'cancel',
        }
      ]
    });

    alert.present();
  }

  protected readonly JSON = JSON;
}
